import { useCallback, useMemo } from 'react';
import { Feature } from 'common/models/page-config.models';
import { COACH_VIEW_ITEM_FEATURES, ENV } from 'common/constants';
import { ColumnModel } from 'common/models/widget-config.model';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';

interface UseFeatureFlags {
  hasFeature: (feature: Feature[] | Feature, matchAll?: boolean) => boolean;
  featureCoachViewItems: (item: ColumnModel) => boolean;
  features: Feature[];
}

function useFeatureFlags(): UseFeatureFlags {
  const { projectFeatures } = useModuleProjectContext();
  const featureFlagOverrides = JSON.parse(
    sessionStorage.getItem('featureFlags') ?? '[]'
  ) as Feature[];
  const devMode = ENV.VITE_DEVELOPMENT_PAGE;

  const hasFeature = useCallback(
    (userFeature: Feature[] | Feature, matchAll: boolean = false): boolean => {
      if (userFeature.length === 0) {
        return true;
      }

      const featuresArray: Feature[] = Array.isArray(userFeature) ? userFeature : [userFeature];

      if (devMode && featureFlagOverrides.length > 0) {
        return featureFlagOverrides.some((feature) => featuresArray.includes(feature));
      }

      if (matchAll) {
        return featuresArray.every((feature) => {
          return projectFeatures.find((userFeature) => userFeature === feature);
        });
      }

      return featuresArray.some((feature) => {
        return projectFeatures.find((userFeature) => userFeature === feature);
      });
    },
    // Disabled because we're stringifying the values as they're not memoized properly
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(projectFeatures)]
  );

  const featureCoachViewItems = useCallback(
    (item: ColumnModel): boolean => {
      const { title: id } = item;

      return hasFeature(COACH_VIEW_ITEM_FEATURES?.[id ?? ''] ?? []);
    },
    [hasFeature]
  );

  return useMemo(
    () => ({
      hasFeature,
      featureCoachViewItems,
      features: projectFeatures,
    }),
    [hasFeature, featureCoachViewItems, projectFeatures]
  );
}

export default useFeatureFlags;
